(() => {
  if (customElements.get('ls-room-stories')) return;

  class RoomStories extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.products = new Map();
      this.busy = false;
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
        style: 'currency', currency: this.dataset.currency || 'INR', maximumFractionDigits: 2,
      });
      this.querySelectorAll('[data-room-dialog]').forEach(dialog => {
        const data = JSON.parse(dialog.querySelector('[data-room-product-data]').textContent);
        const form = dialog.querySelector('[data-room-form]');
        const item = { data, dialog, form, variant: data.variants.find(v => String(v.id) === form.elements.id.value) };
        this.products.set(dialog.dataset.productId, item);
        dialog.addEventListener('close', () => {
          if (dialog.open) return;
          this.querySelectorAll(`[aria-controls="${dialog.id}"]`).forEach(dot => dot.setAttribute('aria-expanded', 'false'));
          if (this.activeDialog === dialog) {
            this.activeDialog = null;
            this.opener?.focus({ preventScroll: true });
          }
        }, options);
        this.renderProduct(item);
      });
      this.addEventListener('click', event => this.onClick(event), options);
      this.addEventListener('change', event => this.onChange(event), options);
      this.addEventListener('submit', event => {
        const form = event.target.closest('[data-room-form]');
        if (!form) return;
        event.preventDefault();
        event.stopPropagation();
        const item = this.products.get(form.closest('[data-room-dialog]').dataset.productId);
        this.addItems([item], form.querySelector('[data-room-status]'));
      }, options);
      const bar = this.querySelector('[data-room-buybar]');
      if (bar) bar.hidden = this.products.size === 0;
      this.renderTotal();
    }

    disconnectedCallback() {
      this.activeDialog?.close();
      this.events?.abort();
      this.events = null;
    }

    onClick(event) {
      const dot = event.target.closest('[data-room-dot]');
      if (dot) {
        const dialog = document.getElementById(dot.getAttribute('aria-controls'));
        if (!dialog?.showModal) return;
        event.preventDefault();
        this.activeDialog?.close();
        this.opener = dot;
        this.activeDialog = dialog;
        dot.setAttribute('aria-expanded', 'true');
        dialog.showModal();
        dialog.scrollTop = 0;
      }
      const dialog = event.target.closest('[data-room-dialog]');
      if (dialog && (event.target.closest('[data-room-close]') || event.target === dialog)) {
        if (event.target === dialog) {
          const rect = dialog.getBoundingClientRect();
          if (event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom) return;
        }
        dialog.close();
      }
      const thumbnail = event.target.closest('[data-room-image]');
      if (thumbnail) this.setImage(dialog, thumbnail.dataset.roomImage, thumbnail.dataset.roomAlt);
      if (event.target.closest('[data-room-buy-all]')) this.addItems([...this.products.values()], this.querySelector('[data-room-look-status]'));
    }

    onChange(event) {
      if (!event.target.matches('[data-room-option]')) return;
      const dialog = event.target.closest('[data-room-dialog]');
      const item = this.products.get(dialog.dataset.productId);
      const options = [...item.form.querySelectorAll('[data-room-option]')].map(input => input.value);
      item.variant = item.data.variants.find(variant => variant.options.every((value, i) => value === options[i]));
      item.form.querySelector('[data-room-status]').textContent = '';
      this.renderProduct(item);
      this.renderTotal();
    }

    available(item) {
      return Boolean(item.variant?.available && !item.data.requires_selling_plan);
    }

    renderProduct(item) {
      const { dialog, form, variant } = item;
      const available = this.available(item);
      form.elements.id.value = variant?.id || '';
      dialog.querySelector('[data-room-price]').textContent = variant ? this.money.format(variant.price / 100) : 'Unavailable combination';
      const compare = dialog.querySelector('[data-room-compare]');
      compare.hidden = !(variant?.compare_at_price > variant?.price);
      compare.textContent = variant ? this.money.format((variant.compare_at_price || 0) / 100) : '';
      const button = form.querySelector('[data-room-add]');
      button.disabled = this.busy || !available;
      button.textContent = this.busy ? 'Please wait...' : available ? 'Add to Cart' : item.data.requires_selling_plan ? 'Choose a plan on product page' : variant ? 'Sold Out' : 'Unavailable';
      form.querySelector('[data-room-stock]').textContent = available ? 'In stock' : item.data.requires_selling_plan ? 'This product requires a selling plan.' : 'This selection is not available.';
      form.querySelectorAll('[data-room-option]').forEach(select => { select.disabled = this.busy; });
      const link = dialog.querySelector('[data-room-product-link]');
      link.href = `${this.root()}products/${encodeURIComponent(item.data.handle)}${variant ? `?variant=${variant.id}` : ''}`;
      if (variant?.featured_image?.src) this.setImage(dialog, variant.featured_image.src, variant.featured_image.alt || item.data.title);
    }

    setImage(dialog, src, alt) {
      const image = dialog?.querySelector('.ls-room-stories__hero');
      if (!image) return;
      image.removeAttribute('srcset');
      image.src = src;
      image.alt = alt || '';
      dialog.querySelectorAll('[data-room-image]').forEach(button => {
        const path = url => new URL(url, location.href).pathname;
        button.setAttribute('aria-pressed', String(path(button.dataset.roomImage) === path(src)));
      });
    }

    renderTotal() {
      const total = this.querySelector('[data-room-total]');
      if (!total) return;
      const items = [...this.products.values()];
      const complete = items.every(item => item.variant);
      total.textContent = complete ? this.money.format(items.reduce((sum, item) => sum + item.variant.price, 0) / 100) : 'Choose available options';
      this.querySelector('[data-room-count]').textContent = `${items.length} ${items.length === 1 ? 'product' : 'products'}`;
      const unavailable = this.dataset.incomplete === 'true' || items.some(item => !this.available(item));
      const button = this.querySelector('[data-room-buy-all]');
      button.disabled = this.busy || unavailable || items.length === 0;
      button.textContent = this.busy ? 'Please wait...' : unavailable ? 'Look unavailable' : 'Buy the Look';
      const status = this.querySelector('[data-room-look-status]');
      if (unavailable && !this.busy) status.textContent = 'One or more selections are unavailable. You can still add available products individually.';
      else if (status.textContent.startsWith('One or more selections')) status.textContent = '';
    }

    root() { return window.Shopify?.routes?.root || '/'; }

    async json(url, options = {}) {
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options });
      const data = await response.json();
      if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : typeof data.message === 'string' ? data.message : 'The store could not complete this request.');
      return data;
    }

    async refreshCart() {
      const cart = await this.json(`${this.root()}cart.js`);
      document.dispatchEvent(new CustomEvent('theme:cart:change', { detail: { cart }, bubbles: true }));
      document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
      return cart;
    }

    async addItems(items, status) {
      if (this.busy || !items.length || items.some(item => !this.available(item))) return;
      if (status.matches('[data-room-look-status]') && this.dataset.incomplete === 'true') return;
      this.busy = true;
      this.products.forEach(item => this.renderProduct(item));
      this.renderTotal();
      status.textContent = 'Checking availability...';
      let submitted = false;
      let added = false;
      try {
        // Recheck every product before a multi-item add; never retry a cart POST automatically.
        const fresh = await Promise.all(items.map(item => this.json(`${this.root()}products/${encodeURIComponent(item.data.handle)}.js`)));
        let changedPrice = false;
        fresh.forEach((data, i) => {
          const item = items[i];
          const variant = data.variants.find(v => v.id === item.variant.id);
          if (variant && variant.price !== item.variant.price) changedPrice = true;
          item.data.variants = data.variants;
          item.data.requires_selling_plan = data.requires_selling_plan;
          item.variant = variant;
          this.renderProduct(item);
        });
        this.renderTotal();
        if (items.some(item => !this.available(item))) throw new Error('An item is no longer available. Nothing was added. Please choose another option or add available products individually.');
        if (changedPrice) throw new Error('A price has changed. The total is now updated; please review it and click again to add.');
        if (!this.isConnected) return;
        status.textContent = 'Adding to cart...';
        submitted = true;
        await this.json(`${this.root()}cart/add.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: items.map(item => ({ id: item.variant.id, quantity: 1 })) }),
        });
        added = true;
        status.textContent = items.length === 1 ? 'Added to your cart.' : `${items.length} products added to your cart.`;
        this.querySelectorAll('[data-room-cart-link]').forEach(link => { link.hidden = false; });
        try {
          await this.refreshCart();
          if (this.isConnected) {
            this.activeDialog?.close();
            await new Promise(resolve => requestAnimationFrame(resolve));
            document.querySelector('[data-drawer="drawer-cart"]')?.dispatchEvent(new CustomEvent('theme:drawer:open', { bubbles: true }));
          }
        } catch (_) {
          status.textContent += ' Open View cart to see your items.';
        }
      } catch (error) {
        status.textContent = error.message;
        if (submitted && !added) {
          status.textContent += ' Please check your cart before trying again; some items may already have been added.';
          this.querySelectorAll('[data-room-cart-link]').forEach(link => { link.hidden = false; });
          try { await this.refreshCart(); } catch (_) { /* Keep the cart link available if refresh fails. */ }
        }
      } finally {
        this.busy = false;
        this.products.forEach(item => this.renderProduct(item));
        this.renderTotal();
      }
    }
  }
  customElements.define('ls-room-stories', RoomStories);
})();
